import React, { useContext, useEffect, useState } from "react";
import styles from "./AssetDeviceCard.module.scss";
import StatusIndicator from "./../StatusIndicator";
import moment from "moment";
import {
  SENTINEL_MOBILE_TRACKER,
  SENTINEL_MOBILE,
  SENTINEL_TRACKER,
} from "../../constants/sentinel_types";
import {
  INACTIVE_EXPIRED_LICENSE,
  DEPLOYED,
  HALF_DEPLOYED,
  NOT_DEPLOYED,
} from "../../constants/statuses";
import Icon from "../Icon";
import Loading from "../Loading";
import getStatus from "../../helpers/statuses";
import Label from "../Label";
import InputRow from "../InputRow";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import { put } from "../../api";
import { useConfirm } from "material-ui-confirm";
import { AuthContext } from "../../contexts/Auth";
import { hasPermission } from "../../helpers/authorization";
import {
  NOTIFICATION_PAUSE_CHANGED,
  fetchActivePause,
  isActivePause,
  pausedUntilText,
  spanText,
  unpauseDevice,
} from "../../helpers/notificationPause";

/**
 * A burst of impacts pauses a device's impact alerts for a while. The pause
 * starts and ends on the server, so it is re-checked this often while the
 * page is open, and straight away when it is ended elsewhere on the page.
 */
const PAUSE_POLL_MS = 60 * 1000;

export default function AssetDeviceCard({
  device,
  asset,
  groups,
  license = null,
  signal = null,
  battery = null,
  temperature = null,
  firmware = null,
  refresh = null,
}) {
  const [notificationEnabled, setNotificationEnabled] = useState(
    !asset?.maintenance
  );
  const [isLoading, setIsLoading] = useState(false);
  const confirm = useConfirm();
  const { state: currentUser } = useContext(AuthContext);
  const canSeePause = hasPermission(currentUser?.user?.perms || [], "DEVICE_R");
  const [pause, setPause] = useState(null);
  const [unpausing, setUnpausing] = useState(false);

  useEffect(() => {
    if (!device?.id || !canSeePause) return;
    let cancelled = false;
    const load = async () => {
      const active = await fetchActivePause(device.id);
      if (!cancelled) setPause(active);
    };
    const onChanged = (e) => {
      if (e.detail?.deviceId === device.id) load();
    };
    load();
    const timer = setInterval(load, PAUSE_POLL_MS);
    window.addEventListener(NOTIFICATION_PAUSE_CHANGED, onChanged);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener(NOTIFICATION_PAUSE_CHANGED, onChanged);
    };
  }, [device?.id, canSeePause]);

  const alertsPaused = isActivePause(pause);

  const handleUnpause = async () => {
    if (unpausing) return;
    setUnpausing(true);
    if (await unpauseDevice(device.id)) setPause(null);
    setUnpausing(false);
  };

  const displayLicenceSimple = (license) => {
    return license
      ? moment.utc(license?.expiry_date).format("MMM Do YYYY")
      : "N/A";
  };

  const handleToggleNotification = async (state) => {
    if (!confirm) return;

    try {
      confirm({
        description: `Are you sure you want to ${!state ? "enable" : "disable "
          } alerts?`,
        title: null,
        dialogProps: { fullScreen: false },
        cancellationButtonProps: {
          color: "error",
          disableRipple: true,
        },
        confirmationButtonProps: {
          color: "primary",
          disableRipple: true,
        },
      })
        .then(async () => {
          setIsLoading(true);
          console.log(
            "Toggling notification for asset:",
            asset.id,
            "to",
            state
          );
          await put(`/assets/${asset.id}/maintenance`, {
            enable: state,
          });

          if (refresh) {
            refresh();
          }
          setNotificationEnabled(!state);

          setTimeout(() => {
            setIsLoading(false);
          }, 500);
        })
        .catch(() => {
          setIsLoading(false);
        });
    } catch (e) {
      // User cancelled or error
    } finally {
      setIsLoading(false);
    }
  };

  const sentinelMobile =
    device?.devicetype_id === SENTINEL_MOBILE ||
    device?.devicetype_id === SENTINEL_MOBILE_TRACKER;

  const sentinelTracker = device?.devicetype_id === SENTINEL_TRACKER;
  return (
    <div className={styles.deviceCard}>
      {/* Header row: Serial number on the left, icons on the right */}
      <InputRow>
        <Label text="Serial number" theme="light">
          {device.serial_number || "N/A"}
        </Label>

        <Label text="Expiration" theme="light">
          {displayLicenceSimple(license)}
        </Label>
      </InputRow>
      <div className={styles.break} />
      {!sentinelTracker && (
        <InputRow>
          <Label text="Status" theme="light">
            <div className={styles.wrapvertStatus}>
              {device.status === INACTIVE_EXPIRED_LICENSE ? (
                <span className={styles.danger}>
                  {getStatus(device.status).name}
                </span>
              ) : (
                <>
                  <StatusIndicator status={device.status} size="20px" />
                  {getStatus(device.status).name}
                </>
              )}
            </div>
          </Label>

          <Label
            text={
              <>
                Alerts
                {notificationEnabled && alertsPaused && (
                  // Inside the Alerts <label>: a click here must not reach the toggle.
                  <span
                    className={[styles.paused, unpausing ? styles.busy : null].join(" ")}
                    title={`${pause.impact_count} impacts within ${spanText(
                      pause.window_minutes
                    )}`}
                    onClick={(e) => e.preventDefault()}
                  >
                    <span>· Paused until {pausedUntilText(pause.paused_until)} ·</span>
                    <span className={styles.unpauseWrap}>
                      {/* Not a <button>: that would become the control the Alerts label clicks, in place of the toggle. */}
                      <span
                        role="button"
                        tabIndex={unpausing ? -1 : 0}
                        aria-disabled={unpausing}
                        className={styles.unpause}
                        onClick={handleUnpause}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleUnpause();
                          }
                        }}
                      >
                        Resume
                      </span>
                      {unpausing && (
                        // Out of the flow, so it appearing doesn't resize the card's columns.
                        <span className={styles.unpausing} role="status" aria-label="Resuming">
                          <Loading color="dark" size={14} />
                        </span>
                      )}
                    </span>
                  </span>
                )}
              </>
            }
            theme="light"
          >
            <div className={styles.wrapvertStatus}>
              <i
                className={`material-icons`}
                style={{
                  fontSize: !asset.maintenance ? 26 : 24,
                  color: `${asset?.maintenance ? "#ee0e0e" : "#05b008"}`,
                }}
                aria-hidden="true"
              >
                {!asset?.maintenance ? "gpp_good" : "remove_moderator"}
              </i>
              <div className={styles.wrapvertStatus}>
                <ToggleSwitch
                  isOn={notificationEnabled}
                  handleToggle={() =>
                    handleToggleNotification(notificationEnabled)
                  }
                  onColor="#05b008"
                />
              </div>
            </div>
            <div className={styles.spacer} />
          </Label>

          {sentinelMobile && (
            <Label text="Deployment" theme="light">
              <div className={styles.wrapvertStatus}>
                <StatusIndicator
                  status={
                    asset?.deployed === 0
                      ? DEPLOYED
                      : asset?.deployed === 4
                        ? HALF_DEPLOYED
                        : NOT_DEPLOYED
                  }
                  size="20px"
                />

                {asset?.deployed === 0
                  ? "Deployed"
                  : asset?.deployed === 4
                    ? "Half Deployed"
                    : "Not deployed"}
              </div>
            </Label>
          )}
        </InputRow>
      )}

      <InputRow>
        <Label text="Groups" theme="light">
          {groups}
        </Label>
      </InputRow>

      {device && (
        <>
          <div className={styles.break} />
          <div className={styles.deviceCardIcons}>
            {signal && <div className={styles.icon}>{signal}</div>}
            {battery && <div className={styles.icon}>{battery}</div>}
            {temperature && <div className={styles.icon}>{temperature}</div>}
            {firmware && <div className={styles.icon}>{firmware}</div>}
          </div>
        </>
      )}
    </div>
  );
}
